import React, { useState, useEffect, useMemo, useRef, useImperativeHandle } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import NewLoader from "../../../../../../../CommonComponents/NewLoader";
import type { ThermalModelConfig } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/types/ThermalConfig";
import type { ExposureTiming } from "../../../../../../../CommonComponents/orbital-insights/orbital-insights-type";
import { SatGizmoAxes as SatGizmo } from "../../../../../../../Missions/Satellites/SatelliteProfile/SatelliteDesigner/ThermalDesigner/components/visualization";
import type { HeatmapLayerState, RenderingQualitySettings } from "../../hooks/useThermalFilters";
import { ThermalControlsBar } from "./ThermalControlsBar";
import { HeatmapRow, formatTime } from "../../ThermalHeatMap";
import { FACE_ORDER, LEGEND_BG, EMPTY_NODES } from "../../constants/thermalConstants";
import { useThrottledValue, useSyncedCameraState, useSmoothedBodyRotation } from "../../hooks/CubeAndFaceHooks";
import FloorDotGrid from "./FloorDotGrid";
import { CubeWireframe, CubeAxes, FaceBlend, FaceGhost, FaceGrid, InternalDecksHeatmap, DeckLabels, SolarPanels } from "./CubeAndFaceComponents";

export interface ThermalHeatMap3dViewProps {
    currentRow: HeatmapRow | undefined;
    jumpVersion: number;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    isPlaying: boolean;
    isLastStep: boolean;
    playbackSpeed: number;
    isSynced: boolean;
    viewMode?: "normal" | "compact" | "fullscreen";
    isSelectedView: { is3dView: boolean; isChartView: boolean; isMarginsView: boolean };
    thermalModel?: ThermalModelConfig;
    layerState: HeatmapLayerState;
    faceViews: { showExternal: boolean; showInternal: boolean };
    renderingSettings: RenderingQualitySettings;
    currentExposure?: ExposureTiming | null;
    /** Spacecraft body orientation for the current step (undefined = default pose) */
    bodyQuaternion?: THREE.Quaternion;
    onToggleSync: () => void;
    onTogglePlay: () => void;
    onCycleSpeed: () => void;
    onViewChange: (viewId: "3d" | "charts" | "margins") => void;
}

export interface ThermalHeatMap3dViewHandle {
    /**
     * Returns a PNG data URL of the current canvas pixels, or null if not available.
     * `pane` selects which canvas to read when viewMode === "fullscreen" (two canvases
     * mounted at once); ignored in "normal"/"compact" mode where there is only one canvas.
     * Because the renderer uses frameloop="demand", the caller is responsible for driving
     * props to the desired frame and waiting for the render to actually land before calling
     * this — this method only reads whatever is currently in the buffer.
     */
    captureSnapshot: (pane?: "faces" | "decks") => string | null;
}

// ---------------------------------------------------------------------------
// Helper component to manage camera sync for each canvas
// Uses useThree to access the camera and registers it with the sync hook
const CameraSyncManager: React.FC<{
    mode: "external" | "internal";
    syncCameras?: boolean;
    cameraSync?: {
        registerCamera: (id: string, camera: THREE.Camera, invalidate: () => void) => void;
        unregisterCamera: (id: string) => void;
        syncCameraState: (sourceId: string) => void;
    };
}> = ({ mode, syncCameras, cameraSync }) => {
    const { camera, invalidate } = useThree();
    const orbitControlsRef = React.useRef<any>(null);
    const pendingSyncRef = React.useRef(false);

    // Register camera on mount
    useEffect(() => {
        if (syncCameras && cameraSync) {
            cameraSync.registerCamera(mode, camera, invalidate);
            return () => {
                cameraSync.unregisterCamera(mode);
            };
        }
    }, [mode, syncCameras, cameraSync, camera, invalidate]);

    // Set up OrbitControls change listener for syncing
    useEffect(() => {
        if (!orbitControlsRef.current || !syncCameras || !cameraSync) return;

        const controls = orbitControlsRef.current;
        const handleChange = () => {
            // Invalidate this canvas and batch sync updates using microtask queue
            invalidate();
            if (!pendingSyncRef.current) {
                pendingSyncRef.current = true;
                Promise.resolve().then(() => {
                    cameraSync.syncCameraState(mode);
                    pendingSyncRef.current = false;
                });
            }
        };

        controls.addEventListener('change', handleChange);
        return () => {
            controls.removeEventListener('change', handleChange);
        };
    }, [mode, syncCameras, cameraSync, invalidate]);

    return (
        <OrbitControls 
            ref={orbitControlsRef}
            enablePan={false} 
            makeDefault 
        />
    );
};

// ---------------------------------------------------------------------------
// Shared Three.js scene contents — used by both canvases in dual mode
// ---------------------------------------------------------------------------
interface CubeSceneProps {
    currentNodes: Record<string, number>;
    nodesPerFace: number;
    globalMin: number;
    globalMax: number;
    showNodeTemp: boolean;
    showNadir: boolean;
    showRollPitchYaw: boolean;
    showGizmo: boolean;
    showGrid: boolean;
    showSubsystems: boolean;
    showSolarPanels: boolean;
    showDecks: boolean;
    showFloorGrid: boolean;
    gizmoScale: number;
    /** Rotation of the spacecraft body (faces, decks, subsystems, solar panels, axes). Identity by default. */
    bodyQuaternion?: THREE.Quaternion;
    /** Changes on a time jump, so the body snaps instead of easing */
    snapKey?: number;
    mode: "external" | "internal";
    thermalModel?: ThermalModelConfig;
    syncCameras?: boolean;
    cameraSync?: {
        registerCamera: (id: string, camera: THREE.Camera, invalidate: () => void) => void;
        unregisterCamera: (id: string) => void;
        syncCameraState: (sourceId: string) => void;
    };
}

// Map dprIndex to dpr range: 0=Low(1), 1=Medium(1.5), 2=High(2)
const DPR_VALUES = [[1, 1] as [number, number], [1, 1.5] as [number, number], [1, 2] as [number, number]];

// Canvas background colors for orbital exposure phases
const SUNLIT_BG_COLOR = "rgba(255, 183, 0, 0.10)";   // warm light yellow, very low opacity
const ECLIPSE_BG_COLOR = "rgba(14, 20, 58, 0.45)";    // dark blue
const CANVAS_BG_TRANSITION = "background 1.2s ease-in-out";

// Shared canvas configuration builder — on-demand rendering to avoid redundant full-scene redraws
const getCanvasProps = (settings: RenderingQualitySettings, backgroundColor: string) => ({
    camera: { position: [0.5, 1.8, 2.4] as [number, number, number], fov: 45 },
    gl: { antialias: settings.antialiasing, alpha: true },
    style: { background: backgroundColor, transition: CANVAS_BG_TRANSITION } as React.CSSProperties,
    frameloop: "demand" as const,
    dpr: DPR_VALUES[settings.dprIndex],
});

const CubeScene: React.FC<CubeSceneProps> = React.memo(({
    currentNodes, nodesPerFace, globalMin, globalMax,
    showNodeTemp, showNadir, showRollPitchYaw, showGizmo, showGrid, showSubsystems, showSolarPanels, showDecks, showFloorGrid, gizmoScale, bodyQuaternion, snapKey = 0, mode, thermalModel, syncCameras = true,
    cameraSync,
}) => {
    const anyAxisVisible = showNadir || showRollPitchYaw;
    const bodyRef = useSmoothedBodyRotation(bodyQuaternion, snapKey);
    return (
        <>
            <ambientLight intensity={1.5} />
            {showFloorGrid && <FloorDotGrid floorY={-0.65} extent={2.5} step={0.22} dotSize={0.15} />}
            <group ref={bodyRef}>
                <CubeWireframe />
                {anyAxisVisible && <CubeAxes showNadir={showNadir} showRollPitchYaw={showRollPitchYaw} />}
                {mode === "external"
                    ? FACE_ORDER.map((face) => (
                        <FaceBlend key={face} faceId={face} nodes={currentNodes} nodesPerFace={nodesPerFace} globalMin={globalMin} globalMax={globalMax} showNodeTemp={showNodeTemp} />
                    ))
                    : FACE_ORDER.map((face) => <FaceGhost key={face} faceId={face} />)
                }
                {mode === "external" && showGrid && FACE_ORDER.map((face) => (
                    <FaceGrid key={`grid-${face}`} faceId={face} nodesPerFace={nodesPerFace} />
                ))}
                {showSolarPanels && <SolarPanels />}
                {mode === "internal" && (
                    <InternalDecksHeatmap
                        nodes={currentNodes}
                        nodesPerFace={nodesPerFace}
                        globalMin={globalMin}
                        globalMax={globalMax}
                        showNodeTemp={showNodeTemp}
                        showGrid={showGrid}
                        showSubsystems={showSubsystems}
                        thermalModel={thermalModel}
                    />
                )}
                {mode === "internal" && showDecks && <DeckLabels />}
            </group>
            {syncCameras ? (
                <CameraSyncManager
                    mode={mode}
                    syncCameras={syncCameras}
                    cameraSync={cameraSync}
                />
            ) : (
                <OrbitControls enablePan={false} />
            )}
            {showGizmo && <SatGizmo margin={[45, 48]} scale={gizmoScale} />}
        </>
    );
});

// ---------------------------------------------------------------------------
// Helper component to bridge canvas DOM element from useThree
// ---------------------------------------------------------------------------
function CanvasDomBridge({ target }: { target: React.MutableRefObject<HTMLCanvasElement | null> }) {
    const { gl } = useThree();
    useEffect(() => {
        target.current = gl.domElement;
        return () => { target.current = null; };
    }, [gl]);
    return null;
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

const ThermalHeatMap3dView = React.memo(
    React.forwardRef<ThermalHeatMap3dViewHandle, ThermalHeatMap3dViewProps>(({
        currentRow,
        jumpVersion,
        nodesPerFace,
        globalMin,
        globalMax,
        isPlaying,
        isLastStep,
        playbackSpeed,
        isSynced,
        viewMode = "normal",
        isSelectedView,
        thermalModel,
        layerState,
        faceViews,
        renderingSettings,
        currentExposure,
        bodyQuaternion,
        onToggleSync,
        onTogglePlay,
        onCycleSpeed,
        onViewChange,
    }, ref) => {

    // const [renderingSettings, setRenderingSettings] = useState<RenderingQualitySettings>({
    //     dprIndex: 1,
    //     antialiasing: true,
    //     floorGridEnabled: true,
    //     refreshRateMs: 10000,
    // });

    const isCompactMode = viewMode === "compact";
    const isDualMode = viewMode === "fullscreen";
    const { showGizmo, showNadir, showRollPitchYaw, showGrid, showDecks, showNodeTemp, showSubsystems, showSolarPanels, syncCameras } = layerState;
    const { showExternal, showInternal } = faceViews;

    // Fullscreen shows each face view as its own pane; regular/compact show one canvas in the chosen mode
    const showFacesPane = isDualMode && showExternal;
    const showDecksPane = isDualMode && showInternal;
    const bothPanesShown = showFacesPane && showDecksPane;

    // Refs to capture canvas DOM elements for snapshot API
    const facesDomRef = useRef<HTMLCanvasElement | null>(null);
    const decksDomRef = useRef<HTMLCanvasElement | null>(null);
    const singleDomRef = useRef<HTMLCanvasElement | null>(null);

    // In compact mode the gizmo is always off regardless of layer state
    const effectiveShowGizmo = isCompactMode ? false : showGizmo;

    // Camera sync state for dual-view fullscreen mode
    const cameraSync = useSyncedCameraState(bothPanesShown && (syncCameras ?? true));

    const gizmoScale   = viewMode === "fullscreen" ? 36 : viewMode === "compact" ? 24 : 30;
    const currentNodes = currentRow?.nodes ?? EMPTY_NODES;
    const timeSec      = currentRow?.time_sec ?? 0;

    // Compute canvas background color based on orbital exposure phase
    const canvasBackgroundColor = useMemo(() => {
      if (!currentExposure) return "transparent";
      return currentExposure.exposureType === "Sunlit" ? SUNLIT_BG_COLOR : ECLIPSE_BG_COLOR;
    }, [currentExposure?.exposureType]);

    // Throttle 3D view updates to reduce GPU pressure during sync/playback
    const throttledNodes = useThrottledValue(currentNodes, renderingSettings.refreshRateMs);

    // Track when a deliberate time jump occurs and show loader until throttle catches up
    const [isCatchingUpFromJump, setIsCatchingUpFromJump] = useState(false);
    const prevJumpVersionRef = useRef(jumpVersion);

    useEffect(() => {
      if (jumpVersion !== prevJumpVersionRef.current) {
        prevJumpVersionRef.current = jumpVersion;
        setIsCatchingUpFromJump(true);
      }
    }, [jumpVersion]);

    useEffect(() => {
      if (isCatchingUpFromJump && throttledNodes === currentNodes) {
        setIsCatchingUpFromJump(false);
      }
    }, [throttledNodes, currentNodes, isCatchingUpFromJump]);

    const sceneProps = useMemo<Omit<CubeSceneProps, "mode">>(() => ({
        currentNodes: throttledNodes, nodesPerFace, globalMin, globalMax, bodyQuaternion, snapKey: jumpVersion,
        showNodeTemp, showNadir, showRollPitchYaw, showGizmo: effectiveShowGizmo, showGrid, showSubsystems, showSolarPanels, showDecks, showFloorGrid: renderingSettings.floorGridEnabled, gizmoScale, thermalModel,
        syncCameras: bothPanesShown && syncCameras,
        cameraSync: bothPanesShown ? cameraSync : undefined,
    }), [throttledNodes, nodesPerFace, globalMin, globalMax, showNodeTemp, showNadir, showRollPitchYaw,
         effectiveShowGizmo, showGrid, showSubsystems, showSolarPanels, showDecks, renderingSettings.floorGridEnabled, gizmoScale, bodyQuaternion, jumpVersion, thermalModel, bothPanesShown, syncCameras, cameraSync]);

    useImperativeHandle(ref, () => ({
        captureSnapshot: (pane?: "faces" | "decks") => {
            // Unrendered panes have null refs (cleared by CanvasDomBridge); no pane requested falls back to whichever is shown
            const canvas =
                isDualMode
                    ? (pane === "decks" ? decksDomRef.current : pane === "faces" ? facesDomRef.current : (facesDomRef.current ?? decksDomRef.current))
                    : singleDomRef.current;
            return canvas ? canvas.toDataURL("image/png", 0.95) : null;
        },
    }), [isDualMode]);

    return (
        <div className="thm-3d-view">
            <div className="thm-3d-main-row">
                <div className="thm-3d-legend-col">
                    <span className="thm-3d-legend-label">{globalMax.toFixed(1)}°C</span>
                    <div className="thm-3d-legend-bar" style={{ background: LEGEND_BG }} />
                    <span className="thm-3d-legend-label">{globalMin.toFixed(1)}°C</span>
                </div>

                {isDualMode ? (
                    // Fullscreen: external (left) and/or internal (right), each shown only when selected
                    <div className="thm-3d-dual-canvas-wrapper">
                        {showFacesPane && (
                          <div className="thm-3d-dual-pane">
                              <span className="thm-3d-dual-label">Faces View</span>
                              <Canvas {...getCanvasProps(renderingSettings, canvasBackgroundColor)}>
                                  <CanvasDomBridge target={facesDomRef} />
                                  <CubeScene {...sceneProps} mode="external" />
                              </Canvas>
                          </div>
                        )}
                        {bothPanesShown && <div className="thm-3d-dual-divider" />}
                        {showDecksPane && (
                          <div className="thm-3d-dual-pane">
                              <span className="thm-3d-dual-label">Decks View</span>
                              <Canvas {...getCanvasProps(renderingSettings, canvasBackgroundColor)}>
                                  <CanvasDomBridge target={decksDomRef} />
                                  <CubeScene {...sceneProps} mode="internal" />
                              </Canvas>
                          </div>
                        )}
                        {isCatchingUpFromJump && (
                          <div className="thm-3d-canvas-loading-overlay">
                            <NewLoader canvasHeight="100%" canvasWidth="100%" loaderpage={undefined} />
                          </div>
                        )}
                    </div>
                ) : (
                    // Single canvas (normal / compact)
                    <div className="thm-3d-canvas-wrapper">
                        <Canvas {...getCanvasProps(renderingSettings, canvasBackgroundColor)}>
                            <CanvasDomBridge target={singleDomRef} />
                            <CubeScene
                                {...sceneProps}
                                mode={showInternal ? "internal" : "external"}
                            />
                        </Canvas>
                        {isCatchingUpFromJump && (
                          <div className="thm-3d-canvas-loading-overlay">
                            <NewLoader canvasHeight="100%" canvasWidth="100%" loaderpage={undefined} />
                          </div>
                        )}
                    </div>
                )}

                {/* Right: playback controls */}
                <ThermalControlsBar
                    isPlaying={isPlaying}
                    isLastStep={isLastStep}
                    playbackSpeed={playbackSpeed}
                    isSynced={isSynced}
                    onToggleSync={onToggleSync}
                    onTogglePlay={onTogglePlay}
                    onCycleSpeed={onCycleSpeed}
                />
            </div>

            {/* Step info */}
            <div className="thm-3d-info-row">
                <span>Elapsed time : {formatTime(timeSec)}</span>
            </div>

        </div>
    );
    })
);

ThermalHeatMap3dView.displayName = "ThermalHeatMap3dView";
export default ThermalHeatMap3dView;
